/**
 * Country-driven labels for the portal.
 *
 * The portal was built AU-first, so tax fields said "GST" and "ABN" outright.
 * Flattening those to "Tax" and "Tax ID" stopped them being wrong for GB and
 * US accountants but also made them vaguer for the AU majority. Instead the
 * API now hands back the client's `country_config` and the UI uses the local
 * term: GST/ABN in Australia, VAT/VAT Number in the UK, Sales Tax/EIN in the
 * US. Same source of truth the mobile app and the exports already use
 * (`backend/api/constants/countries.py`).
 */

export interface EntityTypeOption {
  value: string;
  label: string;
}

export interface CountryConfig {
  code: string;
  currency_code: string;
  currency_symbol: string;
  /** Local name for the tax on a receipt: "GST", "VAT", "Sales Tax". */
  tax_label: string;
  /** Local name for a business tax number: "ABN", "VAT Number", "EIN". */
  tax_id_label: string;
  locale: string;
  fiscal_year_start_month: number;
  fiscal_year_start_day: number;
  /** Business structures this country uses, most common first. */
  entity_types?: EntityTypeOption[];
}

/**
 * Used until `country_config` arrives (first render, or an older API). AU is
 * the safe default: it is the portal's launch market and matches what every
 * label said before this became country-aware.
 */
export const DEFAULT_COUNTRY_CONFIG: CountryConfig = {
  code: "AU",
  currency_code: "AUD",
  currency_symbol: "$",
  tax_label: "GST",
  tax_id_label: "ABN",
  locale: "en-AU",
  fiscal_year_start_month: 7,
  fiscal_year_start_day: 1,
  entity_types: [
    { value: "individual", label: "Individual" },
    { value: "sole_trader", label: "Sole trader" },
    { value: "pty_ltd", label: "Pty Ltd" },
    { value: "partnership", label: "Partnership" },
    { value: "trust", label: "Trust" },
  ],
};

export function countryConfigOrDefault(
  config: CountryConfig | null | undefined,
): CountryConfig {
  return config ?? DEFAULT_COUNTRY_CONFIG;
}

/** "GST" / "VAT" / "Sales Tax" — the column header and edit-field label. */
export function taxLabel(config: CountryConfig | null | undefined): string {
  return countryConfigOrDefault(config).tax_label;
}

/** "Total GST" / "Total VAT" — the receipts summary tile. */
export function totalTaxLabel(config: CountryConfig | null | undefined): string {
  return `Total ${taxLabel(config)}`;
}

/** "Vendor ABN" / "Vendor VAT Number" / "Vendor EIN". */
export function vendorTaxIdLabel(
  config: CountryConfig | null | undefined,
): string {
  return `Vendor ${countryConfigOrDefault(config).tax_id_label}`;
}

/**
 * BAS is an Australian filing. The field stays for AU clients and is hidden
 * elsewhere rather than renamed, because no other country has an equivalent
 * quarterly form the same fields would map onto.
 */
export function isAustralia(config: CountryConfig | null | undefined): boolean {
  return countryConfigOrDefault(config).code === "AU";
}

/**
 * An amount with the client's currency symbol: "$12.50" in AU and the US,
 * "£12.50" in the UK. Receipts are stored in the client's own currency (the
 * app sets it from their country), so the symbol comes from the same config.
 * Letter symbols ("CHF", "kr") get a space so they don't run into the digits.
 */
export function formatAmount(
  config: CountryConfig | null | undefined,
  amount: string | number,
): string {
  const symbol = countryConfigOrDefault(config).currency_symbol;
  return /^[A-Za-z]{2,}$/.test(symbol)
    ? `${symbol} ${amount}`
    : `${symbol}${amount}`;
}

/**
 * The referral commission programme runs in Australia only: it is written
 * against the TPB's guidance on disclosing third-party benefits and paid in
 * AUD. Practices elsewhere don't see commission counters they can't earn.
 */
export function offersCommission(
  config: CountryConfig | null | undefined,
): boolean {
  return isAustralia(config);
}

/** Example number in the local format, for the practice phone field. */
const PHONE_PLACEHOLDERS: Record<string, string> = {
  AU: "0412 345 678",
  GB: "07700 900123",
  US: "(212) 555-0123",
};

export function phonePlaceholder(
  config: CountryConfig | null | undefined,
): string {
  return PHONE_PLACEHOLDERS[countryConfigOrDefault(config).code] ?? "";
}

/**
 * Business structures to offer. Falls back to the AU list only when the API
 * has not answered yet — a stored value is never invalidated by this list, so
 * a client already tagged `pty_ltd` keeps that value even if their country
 * later stops offering it.
 */
export function entityTypes(
  config: CountryConfig | null | undefined,
): EntityTypeOption[] {
  const resolved = countryConfigOrDefault(config);
  return resolved.entity_types?.length
    ? resolved.entity_types
    : (DEFAULT_COUNTRY_CONFIG.entity_types ?? []);
}

/**
 * Label for a stored entity value. Looks in the country's list first, then a
 * global fallback, so a value the country no longer offers still renders as a
 * name rather than a raw slug.
 */
export function entityTypeLabel(
  config: CountryConfig | null | undefined,
  value: string,
): string {
  if (!value) return "";
  const inCountry = entityTypes(config).find((t) => t.value === value);
  if (inCountry) return inCountry.label;
  return ALL_ENTITY_TYPE_LABELS[value] ?? value;
}

/** Every value AccountantUser.EntityType allows, for rendering strays. */
const ALL_ENTITY_TYPE_LABELS: Record<string, string> = {
  individual: "Individual",
  sole_trader: "Sole trader",
  pty_ltd: "Pty Ltd",
  partnership: "Partnership",
  trust: "Trust",
  company: "Company",
  llp: "LLP",
  llc: "LLC",
  s_corp: "S corporation",
  c_corp: "C corporation",
};
